import { useEffect, useState } from 'react';

// Enums
import Order from '../../../enums/order.enum';

// Components
import CycleTableTh from '../../TableTh/TableTh';
import getOptions, { CYCLE_POSTWELD_OPTIONS, DEFAULT_BLOCK_PARAMETERS } from '../option';
import { generateIotDataByCycle, isTimestampOutdated, sortArray } from '../utils';
import { EMPTY_IOT_CHANNEL, EMPTY_IOT_THERMOCOUPLE } from '../constants';
import { SortCycleTableProps, TableCycleData } from '../types';
import { CycleController } from '../../../store/models/controllers';
import CycleTableRow from './CycleTableRow';
import generateIdDevice from '../../../utils/generateIdDevice';
import { IControllerSettings } from '../../../types/controller-types';
import CycleStatus from '../../../enums/cycle-status.enum';
import CycleMeasurement from '../../../enums/cycle-measurement';
import { PerChannelData } from '../../Cycle/types';
import { ChannelState } from '../../../enums/channel-state.enum';
import CycleSettings from '../../../enums/cycle-setting.enum';

type ComponentProps = {
  cycleId: string;
  cycle_controllers: CycleController[];
  controllersSettings: Record<string, IControllerSettings>;
  iotDataResponse: any[];
  deviceNames: { [key: string]: string };
  status: CycleStatus;
  measurement?: CycleMeasurement | null;
  channelsIot: Record<string, PerChannelData>;
  testChannel?: (controllerId: string, itemNumber: number) => void;
  canEditChannels?: boolean;
};

const CycleTable = ({
  cycleId,
  cycle_controllers,
  iotDataResponse,
  deviceNames,
  controllersSettings,
  status,
  measurement,
  channelsIot,
  testChannel,
  canEditChannels
}: ComponentProps) => {
  const [isPostWeldExists, setIsPostweldExists] = useState(false);
  const [cycles, setCycles] = useState<TableCycleData[]>([]);
  const CYCLE_OPTION = getOptions(measurement);

  const DEFAULT_SORT: SortCycleTableProps = { key: CYCLE_OPTION.DEGREES.KEY, option: Order.ASC };

  const [sort, setSort] = useState<SortCycleTableProps>(DEFAULT_SORT);

  useEffect(() => {
    if (cycle_controllers.length && iotDataResponse.length) {
      const cycles: TableCycleData[] = [];
      const postweldStates: boolean[] = [];
      cycle_controllers.forEach((cycle_controller) => {
        const serialNumber = cycle_controller.controller.serial_number;

        if (cycle_controller.channels.length > 0) {
          cycle_controller.channels.forEach((channel) => {
            const controllerDisconnected = !cycle_controller.controller.connected;
            const iotChannel = iotDataResponse.find(
              (i) => i.serial === serialNumber && i?.channel && i.channel === channel,
            );
            const isOutdatedData = iotChannel ? isTimestampOutdated(iotChannel.sk) : true;

            const idDevice = generateIdDevice(serialNumber, channel);
            const deviceName = deviceNames[idDevice];

            if (!controllerDisconnected && iotChannel && !isOutdatedData) {
              const pk: string = iotChannel.pk;
              const channelsData = channelsIot[pk];
              const isPostweldEnabled =
                channelsData.iotData.state !== ChannelState.STOPPED &&
                !!channelsData?.settingsByMode[CycleSettings.PostWeld].settings;

              postweldStates.push(isPostweldEnabled);

              const data = generateIotDataByCycle({
                cycleId,
                iot: channelsData.iotData.rawItem,
                name: deviceName,
                cycle_controller,
                device: channel,
                measurement,
                status,
              });

              const tableData: TableCycleData = {
                ...data,
                controllerDisconnected: !cycle_controller.controller.connected,
                status,
                settingMode: channelsData.iotData.mode,
                channelsData: channelsData,
                output_enabled: !channelsData?.moreCHdata?.disabledOutput?.value,
                Pw: channelsData?.moreCHdata?.power_output_scale?.value ?? null,
                Tr: channelsData?.moreCHdata?.trim_channel_setpoint?.value ?? null,
              };
              cycles.push(tableData);
            } else {
              const data = generateIotDataByCycle({
                cycleId,
                iot: EMPTY_IOT_CHANNEL,
                name: deviceName,
                cycle_controller,
                device: channel,
                measurement,
                status,
              });

              const tableData: TableCycleData = {
                ...data,
                controllerDisconnected: !cycle_controller.controller.connected,
                status,
                settingMode: null,
                channelsData: null,
                output_enabled: null,
                Pw: null,
                Tr: null,
              };
              cycles.push(tableData);
            }
          });
        }

        if (cycle_controller.thermocouples.length > 0) {
          cycle_controller.thermocouples.forEach((thermocouple) => {
            const controllerDisconnected = !cycle_controller.controller.connected;
            const idDevice = generateIdDevice(serialNumber, thermocouple);
            const deviceName = deviceNames[idDevice];

            const iotThermocouple = iotDataResponse.find(
              (i) => i.serial === serialNumber && i?.thermocouple && i.thermocouple === thermocouple,
            );
            const isOutdatedData = iotThermocouple ? isTimestampOutdated(iotThermocouple.sk) : true;

            if (!controllerDisconnected && iotThermocouple && !isOutdatedData) {
              const firstThermoCoupleData = iotThermocouple;
              const thermocoupleView = { ...EMPTY_IOT_THERMOCOUPLE, ...firstThermoCoupleData };

              const data = generateIotDataByCycle({
                cycleId,
                iot: thermocoupleView,
                name: deviceName,
                cycle_controller,
                device: thermocouple,
                measurement,
                status,
              });
              const tableData: TableCycleData = {
                ...data,
                controllerDisconnected: !cycle_controller.controller.connected,
                settingMode: null,
                channelsData: null,
                output_enabled: null,
                Pw: null,
                Tr: null,
              };

              cycles.push(tableData);
            } else {
              const data = generateIotDataByCycle({
                cycleId,
                iot: EMPTY_IOT_THERMOCOUPLE,
                name: deviceName,
                cycle_controller,
                device: thermocouple,
                measurement,
                status,
              });
              const tableData: TableCycleData = {
                ...data,
                controllerDisconnected: !cycle_controller.controller.connected,
                settingMode: null,
                channelsData: null,
                output_enabled: null,
                Pw: null,
                Tr: null,
              };

              cycles.push(tableData);
            }
          });
        }
      });
      setIsPostweldExists(postweldStates.some((val) => val));

      setCycles(cycles);
    }
  }, [cycle_controllers, channelsIot, iotDataResponse]);

  const changeSort = (optionKey: string | null, sortOption: SortCycleTableProps) => {
    if (sortOption.key !== optionKey) {
      setSort({ key: optionKey, option: Order.ASC });
      return;
    }

    if (sortOption.key === optionKey) {
      const newOption = sortOption.option === Order.ASC ? Order.DESC : Order.ASC;
      setSort({ key: optionKey, option: newOption });
      return;
    }
  };

  const handleDataChange = (device: TableCycleData, updates: Partial<TableCycleData>) => {
    setCycles(prevCycles =>
      prevCycles.map(cycle =>
        cycle.controllerId === device.controllerId && cycle.itemNumber === device.itemNumber
          ? { ...cycle, ...updates }
          : cycle
      )
    );
  };

  return (
    <div className="c-cycle-table overflow-x-auto text-center font-medium">
      <table className="c-cycle-table__table w-full">
        <thead className="c-cycle-table__thead text-nowrap text-xs leading-6 text-slate-gray dark:text-white">
          <tr className="c-cycle-table__tr-head">
            {Object.keys(CYCLE_OPTION).map((optionKey, idx, arr) => {
              const isLastItem = idx === arr.length - 1;
              const isNeedDefaultBlock = isLastItem && !!isPostWeldExists;
              const thClass = isNeedDefaultBlock ? DEFAULT_BLOCK_PARAMETERS : CYCLE_OPTION[optionKey].BLOCK_PARAMETERS;

              return (
                <CycleTableTh
                  key={optionKey}
                  classNameTh={`${thClass} sticky top-0 z-10 bg-white dark:bg-boxdark`}
                  classNameBlock={`justify-${CYCLE_OPTION[optionKey].ALIGNMENT}`}
                  option={CYCLE_OPTION[optionKey]}
                  dataSort={sort}
                  onClick={changeSort}
                />
              );
            })}
            {!!isPostWeldExists &&
              Object.keys(CYCLE_POSTWELD_OPTIONS).map((optionKey) => (
                <CycleTableTh
                  key={optionKey}
                  classNameTh={`${CYCLE_POSTWELD_OPTIONS[optionKey].BLOCK_PARAMETERS} sticky top-0 z-10 bg-white dark:bg-boxdark`}
                  classNameBlock={`justify-${CYCLE_POSTWELD_OPTIONS[optionKey].ALIGNMENT}`}
                  option={CYCLE_POSTWELD_OPTIONS[optionKey]}
                  onClick={() => {}}
                />
              ))}
          </tr>
        </thead>
        <tbody className="c-cycle-table__tbody whitespace-nowrap text-sm">
          {measurement && sortArray(cycles, sort).map((device, key) => (
            <CycleTableRow
              device={device}
              measurement={measurement}
              index={key}
              key={key}
              controllerFaulted={!!controllersSettings[device.serial]?.faults?.value?.length}
              testChannel={testChannel}
              canEditChannels={canEditChannels}
              onDataChange={handleDataChange}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CycleTable;
